import { useCallback, type MutableRefObject } from "react";
import {
  LiveFlashKind,
  MemberActivityKind,
  TRACKED_PRODUCT_FIELD,
} from "@/types/frontend-types";
import type {
  FlagCollabArrivalsAndAncestorsInput,
  FlagLiveFlash,
  GetAncestorChain,
  IsInUserAttachmentsSubtree,
  PushMemberActivitiesFromCollabsInput,
  PushMemberActivity,
} from "./session-live-signals.types";
import { makeMemberActivityExcerpt } from "./use-session-member-activity-signals";

interface UseSessionCollabLiveSignalsInput {
  ownMessageIdsRef: MutableRefObject<Set<string>>;
  getAncestorChain: GetAncestorChain;
  isInUserAttachmentsSubtree: IsInUserAttachmentsSubtree;
  flagLiveFlash: FlagLiveFlash;
  pushMemberActivity: PushMemberActivity;
}

interface SessionCollabLiveSignals {
  pushMemberActivitiesFromCollabs: (
    input: PushMemberActivitiesFromCollabsInput,
  ) => void;
  flagCollabArrivalsAndAncestors: (
    input: FlagCollabArrivalsAndAncestorsInput,
  ) => void;
}

export function useSessionCollabLiveSignals({
  ownMessageIdsRef,
  getAncestorChain,
  isInUserAttachmentsSubtree,
  flagLiveFlash,
  pushMemberActivity,
}: UseSessionCollabLiveSignalsInput): SessionCollabLiveSignals {
  const pushMemberActivitiesFromCollabs = useCallback(
    (input: PushMemberActivitiesFromCollabsInput): void => {
      input.collabs.forEach((collab) => {
        if (!input.arrivalIds.has(collab.id)) return;
        if (!collab.memberAuthor) return;
        if (ownMessageIdsRef.current.has(collab.id)) return;
        pushMemberActivity({
          memberId: collab.memberAuthor,
          kind: MemberActivityKind.Collab,
          excerpt: makeMemberActivityExcerpt({ text: collab.comment }),
        });
      });
    },
    [ownMessageIdsRef, pushMemberActivity],
  );

  const flagCollabArrivalsAndAncestors = useCallback(
    (input: FlagCollabArrivalsAndAncestorsInput): void => {
      if (shouldSkipCollabArrivalFlash({ input, isInUserAttachmentsSubtree })) {
        return;
      }

      const externalIds = selectExternalCollabIds({
        collabIds: input.newCollabIds,
        ownMessageIdsRef,
      });
      if (externalIds.length === 0) return;

      flagCollabCountArrival({
        productId: input.productId,
        getAncestorChain,
        flagLiveFlash,
      });
    },
    [
      flagLiveFlash,
      getAncestorChain,
      isInUserAttachmentsSubtree,
      ownMessageIdsRef,
    ],
  );

  return { pushMemberActivitiesFromCollabs, flagCollabArrivalsAndAncestors };
}

function shouldSkipCollabArrivalFlash(input: {
  input: FlagCollabArrivalsAndAncestorsInput;
  isInUserAttachmentsSubtree: IsInUserAttachmentsSubtree;
}): boolean {
  if (!input.input.canFlash) return true;
  if (input.input.newCollabIds.length === 0) return true;
  return input.isInUserAttachmentsSubtree(input.input.productId);
}

function selectExternalCollabIds(input: {
  collabIds: readonly string[];
  ownMessageIdsRef: MutableRefObject<Set<string>>;
}): string[] {
  return input.collabIds.filter(
    (collabId) => !input.ownMessageIdsRef.current.has(collabId),
  );
}

function flagCollabCountArrival(input: {
  productId: string;
  getAncestorChain: GetAncestorChain;
  flagLiveFlash: FlagLiveFlash;
}): void {
  [input.productId, ...input.getAncestorChain(input.productId)].forEach(
    (productId) => {
      input.flagLiveFlash({
        kind: LiveFlashKind.Product,
        entityId: productId,
        fields: [TRACKED_PRODUCT_FIELD.CollabCount],
        scopeProductId: productId,
      });
    },
  );
}
